import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, MapPin, Clock, ShieldCheck, Users } from "lucide-react";
import api, { errText } from "@/lib/api";
import { Shell } from "@/components/Shell";

import { SALUTATIONS } from "@/lib/salutations";

const empty = { salutation: "", first_name: "", last_name: "", email: "", phone: "", company: "", job_title: "" };

export default function EventLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [form, setForm] = useState(empty);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [otherSalutation, setOtherSalutation] = useState(false);
  const closed = event?.status === "completed";

  useEffect(() => {
    api
      .get(`/public/events/${slug}`)
      .then(({ data }) => setEvent(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/public/events/${event.event_id}/register`, { ...form, consent });
      if (data.duplicate) {
        toast.info("You're already registered — opening your existing record.");
      } else if (data.registration.status === "waitlisted") {
        toast.warning("Event is full — you've been added to the waitlist.");
      } else if (data.invite_mode) {
        toast.success("Registered! Let us know if you can attend.");
        if (data.email_suppressed) toast.info("The organiser will email you the details shortly.");
        else if (!data.email_sent)
          toast.warning("We couldn't email you just now — this page has everything you need.");
        navigate(`/rsvp/${data.registration.qr_token}`);
        return;
      } else {
        toast.success("Registered! Your QR pass is ready.");
        if (data.email_suppressed) toast.info("The organiser will email your pass shortly.");
        else if (!data.email_sent)
          toast.warning("We couldn't email your pass just now — it's on this page.");
      }
      navigate(`/ticket/${data.registration.qr_token}`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  if (notFound)
    return (
      <Shell>
        <h1 className="font-display text-3xl font-black uppercase" data-testid="event-not-found">
          Event not found
        </h1>
        <Link to="/" className="btn-secondary mt-6">
          Back to events
        </Link>
      </Shell>
    );
  if (!event)
    return (
      <Shell>
        <p className="text-sm text-slate-500">Loading event…</p>
      </Shell>
    );

  const full = event.spots_left <= 0;
  const pct = Math.min(100, Math.round((event.registered / Math.max(event.capacity, 1)) * 100));

  return (
    <Shell wide>
      <div className="rise" data-testid="event-landing">
        <div className="on-image relative rounded-2xl overflow-hidden border border-[#252f3e] h-[280px] sm:h-[380px]">
          {event.key_visual_url && (
            <img src={event.key_visual_url} alt={event.name} className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(to top, #090b0e 0%, rgba(9,11,14,0.72) 45%, rgba(9,11,14,0) 100%)" }}
          />
          <div className="absolute bottom-0 p-6 sm:p-10">
            <p className="overline">{closed ? "Event closed" : full ? "Waitlist open" : "Registration open"}</p>
            <h1
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-none mt-2"
              data-testid="event-title"
            >
              {event.name}
            </h1>
            {event.host_org && (
              <p className="font-mono2 text-xs sm:text-sm text-slate-200 mt-3" data-testid="event-host-org">
                Hosted by {event.host_org}
              </p>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 mt-10">
          <div>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { icon: CalendarDays, label: "Date", value: event.date },
                { icon: Clock, label: "Time", value: `${event.start_time || "—"}${event.end_time ? ` – ${event.end_time}` : ""}` },
                { icon: Users, label: "Capacity", value: `${event.registered}/${event.capacity}` },
              ].map((s) => (
                <div key={s.label} className="card p-4">
                  <p className="label flex items-center gap-1.5">
                    <s.icon size={12} /> {s.label}
                  </p>
                  <p className="font-mono2 text-sm text-slate-100">{s.value}</p>
                </div>
              ))}
            </div>
            <div className="card p-5 mt-4">
              <p className="label flex items-center gap-1.5">
                <MapPin size={12} /> Venue
              </p>
              <p className="text-sm text-slate-200">{event.venue || "To be announced"}</p>
            </div>
            <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight mt-10">About</h2>
            <p className="text-sm sm:text-base text-slate-300 leading-relaxed mt-3 whitespace-pre-line">
              {event.description}
            </p>
            <div className="mt-8">
              <div className="h-2 rounded-full bg-[#1c2430] overflow-hidden">
                <div className="h-full bg-[#00e599] transition-all duration-700" style={{ width: `${pct}%` }} />
              </div>
              <p className="font-mono2 text-xs text-slate-400 mt-2">
                {pct}% filled · {full ? "waitlist only" : `${event.spots_left} spots left`}
              </p>
            </div>
          </div>

          <form onSubmit={submit} className="card p-6 h-fit lg:sticky lg:top-24" data-testid="registration-form">
            {closed ? (
              <div data-testid="event-closed-notice">
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">
                  Registration closed
                </h2>
                <p className="text-sm text-slate-400 mt-3">
                  This event has finished. Already registered? You can still open your pass or leave feedback.
                </p>
                <Link to="/retrieve-qr" className="btn-secondary mt-5" data-testid="closed-retrieve-link">
                  Find my pass
                </Link>
              </div>
            ) : (
              <>
            <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">
              {full ? "Join the waitlist" : "Register"}
            </h2>
            <p className="text-xs text-slate-400 mt-1.5">Your unique QR pass arrives by email instantly.</p>
            <div className="grid sm:grid-cols-3 gap-4 mt-6">
              <div>
                <label className="label">Title (optional)</label>
                <select
                  className="input"
                  data-testid="reg-salutation"
                  value={otherSalutation ? "__other" : form.salutation}
                  onChange={(e) => {
                    if (e.target.value === "__other") {
                      setOtherSalutation(true);
                      setForm({ ...form, salutation: "" });
                    } else {
                      setOtherSalutation(false);
                      setForm({ ...form, salutation: e.target.value });
                    }
                  }}
                >
                  <option value="">—</option>
                  {SALUTATIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                  <option value="__other">Other…</option>
                </select>
                {otherSalutation && (
                  <input
                    className="input mt-2"
                    maxLength={24}
                    placeholder="e.g. Datuk"
                    data-testid="reg-salutation-other"
                    value={form.salutation}
                    onChange={set("salutation")}
                  />
                )}
              </div>
              <div>
                <label className="label">First name</label>
                <input required className="input" data-testid="reg-first-name" value={form.first_name} onChange={set("first_name")} />
              </div>
              <div>
                <label className="label">Last name</label>
                <input required className="input" data-testid="reg-last-name" value={form.last_name} onChange={set("last_name")} />
              </div>
            </div>
            <div className="mt-4">
              <label className="label">Email</label>
              <input required type="email" className="input" data-testid="reg-email" value={form.email} onChange={set("email")} />
            </div>
            <div className="mt-4">
              <label className="label">WhatsApp number (optional)</label>
              <input className="input" placeholder="+6591234567" data-testid="reg-phone" value={form.phone} onChange={set("phone")} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="label">Company</label>
                <input className="input" data-testid="reg-company" value={form.company} onChange={set("company")} />
              </div>
              <div>
                <label className="label">Job title</label>
                <input className="input" data-testid="reg-job-title" value={form.job_title} onChange={set("job_title")} />
              </div>
            </div>
            <label className="flex gap-3 mt-6 text-xs text-slate-400 leading-relaxed cursor-pointer">
              <input
                type="checkbox"
                required
                data-testid="reg-consent"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 accent-[#00e599] w-4 h-4 shrink-0"
              />
              <span>
                I consent to Ark Media using my details to manage my registration, check-in and post-event
                feedback. Data is purged per the{" "}
                <Link to="/privacy" className="text-[#00e599] underline">
                  retention policy
                </Link>
                .
              </span>
            </label>
            <button type="submit" disabled={busy} className="btn-primary w-full mt-6" data-testid="reg-submit-btn">
              {busy ? "Generating pass…" : full ? "Join waitlist" : "Get my QR pass"}
            </button>
            <p className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-4">
              <ShieldCheck size={12} /> Tokens are random and single-use per attendee.
            </p>
              </>
            )}
          </form>
        </div>
      </div>
    </Shell>
  );
}
